import type { SessionBookingTrackingEventName } from "~/server/tracking/session-booking-event";

export const META_PIXEL_ID = "639366498599016";

export const META_EVENT_NAME_MAP = {
  purchase: "SessionBooking",
  repeat_purchase: "RepeatSessionBooking",
  add_to_cart: "ServiceAddedToCart",
  initiate_checkout: "FirstSessionBookingInitiated",
  lead: "QuizSubmitted",
  quiz_complete: "QuizCompleted",
  view_content: "ViewContent",
  registration: "AccountCreated",
  page_view: "PageView",
  payment_failed: "PaymentFailed",
  gift_card_purchase: "GiftCardPurchase",
} as const;

export type MetaConversionTrackingEventName =
  | SessionBookingTrackingEventName
  | "gift_card_purchase";

export interface MetaConversionSnapshot {
  trackingEventName: MetaConversionTrackingEventName;
  adConsentGranted: boolean;
  fbp?: string;
  fbc?: string;
  pageLocation?: string;
  pageReferrer?: string;
}

export interface MetaConversionSnapshotDropReport {
  trackingEventName: MetaConversionTrackingEventName;
  snapshotLength: number;
  maxLength: number;
  hasFbp: boolean;
  hasFbc: boolean;
  hasPageLocation: boolean;
  hasPageReferrer: boolean;
}

const META_PAYMENT_METADATA_KEYS = {
  adConsent: "metaAdConsentGranted",
  fbp: "metaFbp",
  fbc: "metaFbc",
  pageLocation: "metaPageLocation",
  pageReferrer: "metaPageReferrer",
  snapshot: "metaConversionSnapshot",
} as const;

const STRIPE_METADATA_VALUE_MAX_CHARS = 500;

export function createMetaEventId(
  eventName: string,
  transactionId: string,
): string {
  return `meta_${eventName}_${transactionId}`.replace(/[^A-Za-z0-9_-]/g, "_");
}

export function buildMetaConversionSnapshot(props: {
  trackingEventName: MetaConversionTrackingEventName;
  adConsentGranted: boolean;
  attribution?: {
    fbp?: string;
    fbc?: string;
    pageLocation?: string;
    pageReferrer?: string;
  };
}): MetaConversionSnapshot {
  return {
    trackingEventName: props.trackingEventName,
    adConsentGranted: props.adConsentGranted,
    fbp: props.attribution?.fbp || undefined,
    fbc: props.attribution?.fbc || undefined,
    pageLocation: props.attribution?.pageLocation || undefined,
    pageReferrer: props.attribution?.pageReferrer || undefined,
  };
}

export function metaConversionToPaymentMetadata(
  snapshot: MetaConversionSnapshot,
  options?: {
    onSnapshotDropped?: (report: MetaConversionSnapshotDropReport) => void;
  },
): Record<string, string> {
  const serializedSnapshot = serializeMetaConversionSnapshot(snapshot);

  if (serializedSnapshot.length > STRIPE_METADATA_VALUE_MAX_CHARS) {
    const report = {
      trackingEventName: snapshot.trackingEventName,
      snapshotLength: serializedSnapshot.length,
      maxLength: STRIPE_METADATA_VALUE_MAX_CHARS,
      hasFbp: Boolean(snapshot.fbp),
      hasFbc: Boolean(snapshot.fbc),
      hasPageLocation: Boolean(snapshot.pageLocation),
      hasPageReferrer: Boolean(snapshot.pageReferrer),
    };

    console.warn(
      "Meta conversion snapshot exceeded Stripe metadata limit",
      report,
    );
    options?.onSnapshotDropped?.(report);

    return {
      trackingEventName: snapshot.trackingEventName,
    };
  }

  return {
    trackingEventName: snapshot.trackingEventName,
    [META_PAYMENT_METADATA_KEYS.snapshot]: serializedSnapshot,
  };
}

export function buildMetaConversionSnapshotFromMetadata(
  metadata: Record<string, string>,
): MetaConversionSnapshot | null {
  const packedSnapshot = parseMetaConversionSnapshot(
    metadata[META_PAYMENT_METADATA_KEYS.snapshot],
  );
  if (packedSnapshot) {
    return packedSnapshot;
  }

  const trackingEventName = metadata.trackingEventName;
  if (
    trackingEventName !== "purchase" &&
    trackingEventName !== "repeat_purchase" &&
    trackingEventName !== "gift_card_purchase"
  ) {
    return null;
  }

  return {
    trackingEventName,
    adConsentGranted: metadata[META_PAYMENT_METADATA_KEYS.adConsent] === "true",
    fbp: metadata[META_PAYMENT_METADATA_KEYS.fbp] || undefined,
    fbc: metadata[META_PAYMENT_METADATA_KEYS.fbc] || undefined,
    pageLocation:
      metadata[META_PAYMENT_METADATA_KEYS.pageLocation] || undefined,
    pageReferrer:
      metadata[META_PAYMENT_METADATA_KEYS.pageReferrer] || undefined,
  };
}

export function buildMetaAttributionFromMetadata(
  metadata: Record<string, string>,
): {
  fbp?: string;
  fbc?: string;
  pageLocation?: string;
  pageReferrer?: string;
} {
  const snapshot = buildMetaConversionSnapshotFromMetadata(metadata);
  if (!snapshot) {
    return {};
  }

  return {
    fbp: snapshot.fbp,
    fbc: snapshot.fbc,
    pageLocation: snapshot.pageLocation,
    pageReferrer: snapshot.pageReferrer,
  };
}

export function metaConsentToPaymentMetadata(
  adConsentGranted: boolean,
): Record<string, string> {
  return {
    [META_PAYMENT_METADATA_KEYS.adConsent]: adConsentGranted ? "true" : "false",
  };
}

export function buildMetaAdConsentFromMetadata(
  metadata: Record<string, string>,
): boolean | undefined {
  if (metadata.trackingAdConsentGranted !== undefined) {
    return metadata.trackingAdConsentGranted === "true";
  }

  const packedSnapshot = parseMetaConversionSnapshot(
    metadata[META_PAYMENT_METADATA_KEYS.snapshot],
  );
  if (packedSnapshot) {
    return packedSnapshot.adConsentGranted;
  }

  if (metadata[META_PAYMENT_METADATA_KEYS.adConsent] === undefined) {
    return undefined;
  }

  return metadata[META_PAYMENT_METADATA_KEYS.adConsent] === "true";
}

export function serializeMetaConversionSnapshot(
  snapshot: MetaConversionSnapshot,
): string {
  // Compact keys keep this payload inside Stripe's 500-character value limit.
  return JSON.stringify({
    v: 1,
    e: snapshot.trackingEventName,
    c: snapshot.adConsentGranted ? 1 : 0,
    p: snapshot.fbp,
    b: snapshot.fbc,
    l: snapshot.pageLocation,
    r: snapshot.pageReferrer,
  });
}

export function parseMetaConversionSnapshot(
  serialized: string | null | undefined,
): MetaConversionSnapshot | null {
  if (!serialized) {
    return null;
  }

  try {
    const parsed = JSON.parse(serialized) as {
      v?: number;
      e?: string;
      c?: number;
      p?: string;
      b?: string;
      l?: string;
      r?: string;
    };

    if (
      parsed.v !== 1 ||
      (parsed.e !== "purchase" &&
        parsed.e !== "repeat_purchase" &&
        parsed.e !== "gift_card_purchase")
    ) {
      return null;
    }

    return {
      trackingEventName: parsed.e,
      adConsentGranted: parsed.c === 1,
      fbp: parsed.p || undefined,
      fbc: parsed.b || undefined,
      pageLocation: parsed.l || undefined,
      pageReferrer: parsed.r || undefined,
    };
  } catch {
    return null;
  }
}
